import { useState } from "react"
import { Eye } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"

// An eye icon button that opens a modal previewing the attached media —
// the image inline, or a playable <video> for video attachments. Shared by
// every place attachments are listed (the editor list + the article featured
// image). Renders nothing if the attachment has no stored file yet.

type Props = {
  url: string | null
  filename: string | null
  kind: "image" | "video" | "featured_image"
  contentType?: string | null
  // Controlled mode: when `open` is supplied, the component renders only the
  // modal (no built-in Eye trigger) and the caller owns the open state. This
  // lets a custom trigger that lives inside a clickable card (e.g. a video
  // play-thumb inside an <a>) drive the modal without nesting a Radix trigger
  // — which would otherwise fight the card's own click handling.
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

export function AttachmentViewer({ url, filename, kind, contentType, open: openProp, onOpenChange }: Props) {
  const [openState, setOpenState] = useState(false)
  if (!url) return null

  const isControlled = openProp !== undefined
  const open = isControlled ? openProp : openState
  const setOpen = onOpenChange ?? setOpenState

  const isVideo = kind === "video" || (contentType?.startsWith("video/") ?? false)
  const label = filename ?? "Attachment"

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!isControlled && (
        <DialogTrigger asChild>
          <Button variant="ghost" size="sm" aria-label={`View ${label}`}>
            <Eye className="h-4 w-4" />
          </Button>
        </DialogTrigger>
      )}
      <DialogContent size="lg">
        <DialogTitle className="truncate pr-8">{label}</DialogTitle>
        <DialogDescription className="sr-only">Preview of {label}</DialogDescription>
        <div className="mt-2 flex justify-center">
          {isVideo ? (
            <video
              src={url}
              controls
              className="max-h-[70vh] max-w-full rounded-md"
            />
          ) : (
            <img
              src={url}
              alt={label}
              className="max-h-[70vh] max-w-full rounded-md object-contain"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
